import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, leadsAPI } from '../services/api';
import { Plus, Edit, Trash2, Calendar, CheckCircle, Clock, X, ListTodo, Building2, Users } from 'lucide-react';
import PageOptionsButton from './PageOptionsButton';
import { bdaOptions } from '../utils/bdeOwnership';

const Highlights = ({ onRefreshReady, onExportExcel, onExportPDF, showExport = false, onNavigateToPreSales }) => {
  const { user, isAdmin, getAllUsers } = useAuth();
  
  // Helper function to get today's date in local timezone (YYYY-MM-DD)
  const getTodayLocalDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState(null);
  const [selectedBDE, setSelectedBDE] = useState('All');
  const [selectedDate, setSelectedDate] = useState(''); // Date filter
  const [bdeUsers, setBdeUsers] = useState([]);
  const [myBdeId, setMyBdeId] = useState(null); // Resolved BDE id for the logged-in non-admin user
  const [myBdeResolved, setMyBdeResolved] = useState(false); // True once we've attempted to match the logged-in user
  
  const [formData, setFormData] = useState({
    highlight_date: getTodayLocalDate(),
    highlights: '',
    pending_tasks: '',
    notes: '',
    accomplishment_lead_id: '',
    accomplishment_poc_name: ''
  });

  const [todoItems, setTodoItems] = useState([]); // Array of {id, text, completed}
  const [preSalesLeads, setPreSalesLeads] = useState([]); // for Accomplishment company dropdown
  const [companySearch, setCompanySearch] = useState('');
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = React.useRef(null);

  // Fetch pre-sales leads once for the Accomplishment company dropdown + for navigating back on click.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await leadsAPI.getAll();
        const leadsData = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
        if (!cancelled) setPreSalesLeads(leadsData);
      } catch (err) {
        console.error('Failed to load pre-sales leads for Highlights dropdown:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const findLeadById = (leadId) => {
    if (!leadId) return null;
    return preSalesLeads.find(l => String(l.id) === String(leadId)) || null;
  };

  // When a company is chosen in the dropdown, prefill the POC name from the lead (still editable).
  const handleAccomplishmentCompanyChange = (leadId) => {
    const lead = findLeadById(leadId);
    setFormData(prev => ({
      ...prev,
      accomplishment_lead_id: leadId,
      // Only overwrite POC when empty or matching the previous lead's POC, so user typing isn't clobbered.
      accomplishment_poc_name: (!prev.accomplishment_poc_name || prev.accomplishment_poc_name === findLeadById(prev.accomplishment_lead_id)?.poc_name)
        ? (lead?.poc_name || '')
        : prev.accomplishment_poc_name
    }));
  };

  // Close company dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) setCompanyDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Build display label for a lead: "Company (City, Country)". POC lives in its own field.
  const buildLeadLabel = (lead) => {
    if (!lead) return '';
    let label = lead.company_name || `Lead #${lead.id}`;
    const loc = [lead.city, lead.country].filter(Boolean).join(', ');
    if (loc) label += ` (${loc})`;
    return label;
  };

  // Sorted + deduplicated leads for dropdown
  const sortedLeads = React.useMemo(() => {
    return preSalesLeads
      .slice()
      .sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
  }, [preSalesLeads]);

  // Filtered leads for search
  const filteredCompanyLeads = React.useMemo(() => {
    if (!companySearch.trim()) return sortedLeads;
    const q = companySearch.toLowerCase();
    return sortedLeads.filter(l => {
      const name = (l.company_name || '').toLowerCase();
      const poc = (l.poc_name || '').toLowerCase();
      const city = (l.city || '').toLowerCase();
      const country = (l.country || '').toLowerCase();
      return name.includes(q) || poc.includes(q) || city.includes(q) || country.includes(q);
    });
  }, [sortedLeads, companySearch]);

  const handleCompanyClickNavigate = async (leadId) => {
    if (!leadId || !onNavigateToPreSales) return;
    try {
      const res = await leadsAPI.getById(leadId);
      const leadData = res?.data ?? res;
      if (leadData) onNavigateToPreSales(leadData);
    } catch (err) {
      console.error('Failed to open pre-sales lead from Highlights:', err);
    }
  };

  // Match the logged-in user to a BDE record. Prefer email (unique), fall back to name.
  const resolveMyBde = useCallback((bdes) => {
    if (!user) return null;
    const emailLower = (user.email || '').toLowerCase().trim();
    const nameLower = (user.name || '').toLowerCase().trim();
    const userFirstName = nameLower.split(' ')[0];

    // 1. Exact email match
    if (emailLower) {
      const byEmail = bdes.find(b => (b.email || '').toLowerCase().trim() === emailLower);
      if (byEmail) return byEmail;
    }
    // 2. Exact name match
    if (nameLower) {
      const byName = bdes.find(b => (b.name || '').toLowerCase().trim() === nameLower);
      if (byName) return byName;
    }
    // 3. First-name match
    if (userFirstName) {
      const byFirst = bdes.find(b => {
        const bn = (b.name || '').toLowerCase().trim();
        return bn.split(' ')[0] === userFirstName;
      });
      if (byFirst) return byFirst;
    }
    return null;
  }, [user]);

  // Everyone can read everyone's highlights (the team asked for the same
  // "filter by BDE" the Pre-Sales page has). The page OPENS on your own, though:
  // the filter starts on the signed-in person when they are in the BDA list,
  // and on "All BDEs" when they are not (an admin account, or a login that
  // does not match the roster) rather than on an empty screen.
  useEffect(() => {
    const fetchBDEUsers = async () => {
      try {
        const response = await dashboardAPI.getBDEUsers();
        if (response && response.data) {
          setBdeUsers(response.data);
          const matchedBDE = resolveMyBde(response.data);
          setMyBdeId(matchedBDE ? matchedBDE.id : null);
          const inDropdown = matchedBDE && bdaOptions(response.data).some(b => String(b.id) === String(matchedBDE.id));
          setSelectedBDE(inDropdown ? String(matchedBDE.id) : 'All');
        }
      } catch (error) {
        console.error('Error fetching BDE users:', error);
      } finally {
        setMyBdeResolved(true);
      }
    };

    fetchBDEUsers();
  }, [user, resolveMyBde]);

  // Helper function to normalize date for comparison
  const normalizeDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle various date formats
    if (typeof dateStr === 'string') {
      // If it's already YYYY-MM-DD format, return as is
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
        return dateStr.split('T')[0]; // Remove time part if present
      }
      // Try to parse and format
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('Error normalizing date:', dateStr, e);
      }
    }
    return '';
  };

  // Fetch highlights - with date filtering
  const fetchHighlights = useCallback(async () => {
    try {
      setLoading(true);

      const params = {};

      // Only filter by BDE if a specific one is selected in dropdown
      if (selectedBDE && selectedBDE !== 'All') {
        params.bde_id = selectedBDE;
      }

      // Add date parameter if selected
      if (selectedDate) {
        params.date = selectedDate;
      }

      console.log('🔍 Fetching highlights with params:', params);
      const response = await dashboardAPI.getHighlights(params);
      
      if (response && response.success) {
        let highlightsData = response.data || [];
        
        // Client-side date filtering for reliability
        if (selectedDate) {
          const normalizedSelectedDate = normalizeDate(selectedDate);
          highlightsData = highlightsData.filter(highlight => {
            const highlightDate = normalizeDate(highlight.highlight_date);
            return highlightDate === normalizedSelectedDate;
          });
          console.log(`📅 Filtered highlights by date ${selectedDate}: ${highlightsData.length} items`);
        }
        
        console.log('✅ Setting highlights:', highlightsData.length, 'items');
        setHighlights(highlightsData);
      } else {
        console.error('❌ Highlights API error:', response?.error || 'Unknown error');
        setHighlights([]);
      }
    } catch (error) {
      console.error('Error fetching highlights:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBDE, selectedDate]);

  // Fetch on mount and when the filter changes. Wait until we know who is signed
  // in, so the page does not load everyone's highlights and then swap to yours.
  useEffect(() => {
    if (!myBdeResolved) return;
    console.log('🔄 Fetching highlights on mount/BDE change...');
    fetchHighlights();
  }, [fetchHighlights, myBdeResolved]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(fetchHighlights);
    }
    window.highlightsRefresh = fetchHighlights;
    return () => {
      delete window.highlightsRefresh;
    };
  }, [fetchHighlights, onRefreshReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Determine the correct bde_id
      let bdeId;
      if (editingHighlight) {
        // When editing, use the existing bde_id
        bdeId = editingHighlight.bde_id;
      } else if (isAdmin() && selectedBDE !== 'All') {
        // Admin picked a specific BDE from the dropdown
        bdeId = parseInt(selectedBDE);
      } else if (!isAdmin()) {
        // Non-admin: always save under their own resolved BDE (set once on mount).
        if (!myBdeId) {
          alert(`Error: Could not find your BDE user record. Your name is "${user?.name || 'unknown'}" but no matching BDE found. Available BDEs: ${bdeUsers.map(b => b.name).join(', ')}. Please contact admin.`);
          return;
        }
        bdeId = myBdeId;
      } else {
        // Admin with 'All' selected — fall back to their own record if one exists.
        const match = resolveMyBde(bdeUsers);
        if (!match) {
          alert('Error: Select a BDE from the dropdown before saving a highlight.');
          return;
        }
        bdeId = match.id;
      }
      
      // Ensure bde_id is an integer
      if (!bdeId || isNaN(bdeId)) {
        alert('Error: Invalid BDE ID. Please contact admin.');
        return;
      }
      
      // Combine todo items with pending_tasks text
      let finalPendingTasks = formData.pending_tasks;
      if (todoItems.length > 0) {
        const todosText = todosToText(todoItems);
        finalPendingTasks = formData.pending_tasks 
          ? `${formData.pending_tasks}\n${todosText}` 
          : todosText;
      }
      
      const submitData = {
        ...formData,
        pending_tasks: finalPendingTasks,
        bde_id: bdeId,
        accomplishment_lead_id: formData.accomplishment_lead_id || null,
        accomplishment_poc_name: formData.accomplishment_poc_name?.trim() || null
      };
      
      if (editingHighlight) {
        await dashboardAPI.updateHighlight(editingHighlight.id, submitData);
        alert('Highlight updated successfully!');
      } else {
        await dashboardAPI.createHighlight(submitData);
        alert('Highlight created successfully!');
        // Saved under someone other than the person being viewed — show that
        // person's list, or the new highlight would seem to have vanished.
        if (selectedBDE !== 'All' && String(selectedBDE) !== String(bdeId)) {
          setSelectedBDE(String(bdeId));
        }
      }

      setShowForm(false);
      setEditingHighlight(null);
      setFormData({
        highlight_date: getTodayLocalDate(),
        highlights: '',
        pending_tasks: '',
        notes: '',
        accomplishment_lead_id: '',
        accomplishment_poc_name: ''
      });
      setTodoItems([]);
      fetchHighlights();
    } catch (error) {
      console.error('Error saving highlight:', error);
      alert('Error saving highlight: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Parse pending_tasks text into todo items (if it contains bullet points)
  const parseTodosFromText = (text) => {
    if (!text) return [];
    // Check if text contains bullet points or numbered items
    const lines = text.split('\n').filter(line => line.trim());
    const todos = [];
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      // Match bullet points: - item, * item, • item, or numbered: 1. item
      const match = trimmed.match(/^[-*•]\s+(.+)|^\d+\.\s+(.+)|^(.+)$/);
      if (match) {
        const text = match[1] || match[2] || match[3];
        // Check if it starts with [x] or [ ] for completed status
        const completedMatch = text.match(/^\[([xX ])\]\s*(.+)$/);
        if (completedMatch) {
          todos.push({
            id: Date.now() + index,
            text: completedMatch[2],
            completed: completedMatch[1].toLowerCase() === 'x'
          });
        } else {
          todos.push({
            id: Date.now() + index,
            text: text,
            completed: false
          });
        }
      }
    });
    return todos.length > 0 ? todos : [];
  };

  // Convert todo items to text format for storage
  const todosToText = (todos) => {
    if (!todos || todos.length === 0) return '';
    return todos.map(todo => `- [${todo.completed ? 'x' : ' '}] ${todo.text}`).join('\n');
  };

  const handleEdit = (highlight) => {
    setEditingHighlight(highlight);
    const parsedTodos = parseTodosFromText(highlight.pending_tasks);
    setFormData({
      highlight_date: highlight.highlight_date || getTodayLocalDate(),
      highlights: highlight.highlights || '',
      pending_tasks: parsedTodos.length > 0 ? '' : (highlight.pending_tasks || ''),
      notes: highlight.notes || '',
      accomplishment_lead_id: highlight.accomplishment_lead_id ? String(highlight.accomplishment_lead_id) : '',
      accomplishment_poc_name: highlight.accomplishment_poc_name || ''
    });
    setTodoItems(parsedTodos.length > 0 ? parsedTodos : []);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this highlight?')) {
      return;
    }
    
    try {
      setLoading(true);
      await dashboardAPI.deleteHighlight(id);
      alert('Highlight deleted successfully!');
      fetchHighlights();
    } catch (error) {
      console.error('Error deleting highlight:', error);
      alert('Error deleting highlight: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingHighlight(null);
    setFormData({
      highlight_date: getTodayLocalDate(),
      highlights: '',
      pending_tasks: '',
      notes: '',
      accomplishment_lead_id: '',
      accomplishment_poc_name: ''
    });
    setTodoItems([]);
  };

  const addTodoItem = () => {
    setTodoItems([...todoItems, { id: Date.now(), text: '', completed: false }]);
  };

  const removeTodoItem = (id) => {
    setTodoItems(todoItems.filter(item => item.id !== id));
  };

  const updateTodoItem = (id, field, value) => {
    setTodoItems(todoItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const toggleTodoCompleted = (id) => {
    setTodoItems(todoItems.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date string (YYYY-MM-DD) in local timezone to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Daily Highlights</h2>
        <div className="flex gap-4 items-center">
          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-gray-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              placeholder="Filter by date"
            />
            {selectedDate && (
              <button
                onClick={() => setSelectedDate('')}
                className="text-gray-400 hover:text-gray-600"
                title="Clear date filter"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Users size={20} className="text-gray-400" />
            <select
              value={selectedBDE}
              onChange={(e) => setSelectedBDE(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              title="Filter by BDE"
            >
              <option value="All">All BDEs</option>
              {bdaOptions(bdeUsers).map((bde) => (
                <option key={bde.id} value={String(bde.id)}>
                  {bde.name}{String(bde.id) === String(myBdeId) ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b]"
            >
              <Plus size={16} />
              Add Highlight
            </button>
          )}
          <div className="ml-auto flex-shrink-0">
            <PageOptionsButton onExportExcel={onExportExcel} onExportPDF={onExportPDF} showExport={showExport} />
          </div>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingHighlight ? 'Edit Highlight' : 'Add New Highlight'}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.highlight_date}
                onChange={(e) => setFormData({ ...formData, highlight_date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div ref={companyDropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company <span className="text-gray-500 text-xs">(from Pre Sales)</span>
                </label>
                <div
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 cursor-pointer bg-white text-sm flex items-center justify-between"
                  onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
                >
                  <span className={formData.accomplishment_lead_id ? 'text-gray-900' : 'text-gray-400'}>
                    {formData.accomplishment_lead_id
                      ? buildLeadLabel(findLeadById(formData.accomplishment_lead_id))
                      : 'Search or select a company...'}
                  </span>
                  {formData.accomplishment_lead_id && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleAccomplishmentCompanyChange(''); setCompanySearch(''); }}
                      className="text-gray-400 hover:text-gray-600 ml-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
                {companyDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 flex flex-col">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        placeholder="Type to search company, POC, or location..."
                        className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                        autoFocus
                      />
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {filteredCompanyLeads.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-400 text-center">No matches found</div>
                      ) : (
                        filteredCompanyLeads.slice(0, 100).map(lead => {
                          const loc = [lead.city, lead.country].filter(Boolean).join(', ');
                          return (
                            <button
                              key={lead.id}
                              type="button"
                              onClick={() => {
                                handleAccomplishmentCompanyChange(String(lead.id));
                                setCompanyDropdownOpen(false);
                                setCompanySearch('');
                              }}
                              className={`w-full text-left px-3 py-2 hover:bg-[#679632]/5 text-sm flex items-baseline gap-1 ${
                                String(lead.id) === formData.accomplishment_lead_id ? 'bg-[#679632]/10 text-[#679632]' : ''
                              }`}
                            >
                              <span className="font-medium">{lead.company_name || 'Unnamed'}</span>
                              {loc && <span className="text-gray-400 text-xs">({loc})</span>}
                              {lead.poc_name && <span className="text-gray-500 text-xs ml-auto flex-shrink-0">- {lead.poc_name}</span>}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  POC Name <span className="text-gray-500 text-xs">(auto-fills from company, editable)</span>
                </label>
                <input
                  type="text"
                  value={formData.accomplishment_poc_name}
                  onChange={(e) => setFormData({ ...formData, accomplishment_poc_name: e.target.value })}
                  placeholder="Point of contact"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Highlights / Accomplishments * <span className="text-gray-500 text-xs">(What was done today)</span>
              </label>
              <textarea
                required
                rows={4}
                value={formData.highlights}
                onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                placeholder="Enter what you accomplished today, calls made, leads contacted, deals closed, etc."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center justify-between">
                  <span>
                    Pending Tasks <span className="text-gray-500 text-xs">(What's left to do)</span>
                  </span>
                  <button
                    type="button"
                    onClick={addTodoItem}
                    className="flex items-center gap-1 text-sm text-[#679632] hover:text-[#587f2b] font-medium"
                  >
                    <ListTodo size={16} />
                    Add Todo Item
                  </button>
                </div>
              </label>
              
              {/* Todo Items List */}
              {todoItems.length > 0 && (
                <div className="mb-3 space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {todoItems.map((todo) => (
                    <div key={todo.id} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={todo.completed}
                        onChange={() => toggleTodoCompleted(todo.id)}
                        className="mt-1 h-4 w-4 text-[#679632] border-gray-300 rounded focus:ring-[#76AB3F]"
                      />
                      <input
                        type="text"
                        value={todo.text}
                        onChange={(e) => updateTodoItem(todo.id, 'text', e.target.value)}
                        placeholder="Enter todo item..."
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => removeTodoItem(todo.id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Remove todo item"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Text area for additional pending tasks (if no todos or for additional notes) */}
              <textarea
                rows={3}
                value={formData.pending_tasks}
                onChange={(e) => setFormData({ ...formData, pending_tasks: e.target.value })}
                placeholder={todoItems.length > 0 
                  ? "Additional pending tasks or notes (optional)" 
                  : "Enter pending tasks, follow-ups needed, or items to complete later. Or use 'Add Todo Item' button above for structured todos."}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes <span className="text-gray-500 text-xs">(Additional information, reminders, or comments)</span>
              </label>
              <textarea
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Enter any additional notes, reminders, comments, or important information here..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent resize-y"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50"
              >
                {loading ? 'Saving...' : (editingHighlight ? 'Update' : 'Save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && !showForm ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]"></div>
          <p className="mt-2 text-gray-500">Loading highlights...</p>
        </div>
      ) : highlights.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">
            {selectedDate
              ? `No highlights found for ${formatDate(selectedDate)}.`
              : 'No highlights found.'}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b]"
          >
            Add Your First Highlight
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {highlights.map((highlight) => (
            <div
              key={highlight.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={18} className="text-gray-400" />
                    <span className="font-semibold text-gray-900">
                      {formatDate(highlight.highlight_date)}
                    </span>
                    {(highlight.bde_users?.name || highlight.bde_name) && (
                      <span className="text-sm text-gray-500">
                        by {highlight.bde_users?.name || highlight.bde_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {/* Reading is open to everyone; changing stays with the author and admins. */}
                  {(isAdmin() || (myBdeId != null && String(highlight.bde_id) === String(myBdeId)) || (user?.name && (highlight.bde_users?.name || highlight.bde_name) &&
                    (user.name.toLowerCase().trim() === (highlight.bde_users?.name || highlight.bde_name).toLowerCase().trim() ||
                     user.name.toLowerCase().trim().split(' ')[0] === (highlight.bde_users?.name || highlight.bde_name).toLowerCase().trim().split(' ')[0]))) && (
                    <>
                      <button
                        onClick={() => handleEdit(highlight)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Highlight"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(highlight.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Highlight"
                      >
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {highlight.highlights && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={18} className="text-green-600" />
                      <h4 className="font-semibold text-gray-900">Highlights / Accomplishments</h4>
                    </div>
                    {(highlight.accomplishment_lead_id || highlight.accomplishment_poc_name) && (
                      <div className="pl-7 mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        {highlight.accomplishment_lead_id && (() => {
                          const lead = findLeadById(highlight.accomplishment_lead_id);
                          // The server names the company now. The local lookup
                          // is the fallback, and it needs the entire pre-sales
                          // list — which is why a raw lead id used to sit here
                          // for a second or two before turning into a name.
                          const companyName = highlight.accomplishment_company_name
                            || lead?.company_name
                            || (preSalesLeads.length === 0 ? '…' : `Lead #${String(highlight.accomplishment_lead_id).slice(0, 8)}`);
                          return (
                            <button
                              type="button"
                              onClick={() => handleCompanyClickNavigate(highlight.accomplishment_lead_id)}
                              className="inline-flex items-center gap-1 text-[#679632] hover:text-[#587f2b] hover:underline font-medium"
                              title="Open in Pre Sales form"
                            >
                              <Building2 size={14} />
                              {companyName}
                            </button>
                          );
                        })()}
                        {highlight.accomplishment_poc_name && (
                          <span className="text-gray-600">
                            POC: <span className="text-gray-800">{highlight.accomplishment_poc_name}</span>
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-gray-700 whitespace-pre-wrap pl-7">
                      {highlight.highlights}
                    </p>
                  </div>
                )}

                {(() => {
                  // Check if there are any meaningful pending tasks
                  if (!highlight.pending_tasks || !highlight.pending_tasks.trim()) {
                    return null; // Don't show the section if empty
                  }
                  
                  const parsedTodos = parseTodosFromText(highlight.pending_tasks);
                  // Check if we have todos with actual text content
                  const hasValidTodos = parsedTodos.length > 0 && parsedTodos.some(todo => todo.text && todo.text.trim());
                  // Check if there's plain text content (not just whitespace)
                  const hasPlainText = parsedTodos.length === 0 && highlight.pending_tasks.trim();
                  
                  // Only show if we have meaningful content
                  if (!hasValidTodos && !hasPlainText) {
                    return null;
                  }
                  
                  return (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock size={18} className="text-orange-600" />
                        <h4 className="font-semibold text-gray-900">Pending Tasks</h4>
                      </div>
                      <div className="pl-7">
                        {hasValidTodos ? (
                          // Display as checklist (only show todos with actual text)
                          <div className="space-y-2">
                            {parsedTodos
                              .filter(todo => todo.text && todo.text.trim()) // Filter out empty todos
                              .map((todo, index) => (
                                <div key={index} className="flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={todo.completed}
                                    disabled
                                    className="mt-0.5 h-4 w-4 text-[#679632] border-gray-300 rounded"
                                  />
                                  <span className={`text-gray-700 ${todo.completed ? 'line-through text-gray-500' : ''}`}>
                                    {todo.text}
                                  </span>
                                </div>
                              ))}
                          </div>
                        ) : (
                          // Display as plain text
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {highlight.pending_tasks.trim()}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                  {highlight.notes ? (
                    <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border border-gray-200">
                      {highlight.notes}
                    </p>
                  ) : (
                    <p className="text-gray-400 italic text-sm">No notes added</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Highlights;

